<template>
    <div class="campaign-builder-modal">
        <GoalModal
            data-qa="convrrt-page-builder-modal-goal"
            :is-open="isOpen"
            :summary-text="$t('funnel.edit.goal.convrrt.page.builder.message')"
            :title="$t('funnel.edit.goal.convrrt.page.builder.title')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #goalParameters>
                <section class="convrrt-landing-site-select">
                    <DsMultiselect
                        v-model="selectedConvrrtLandingSite"
                        data-qa="convrrt-landing-site"
                        class="select-convrrt-landing-site"
                        searchable
                        required
                        bind-value-only
                        :force-invalid="submitted && !isValidLandingSite"
                        :placeholder="$t('funnel.edit.goal.convrrt.page.builder.site.placeholder')"
                        :loading="loadingConvrrtLandingSites"
                        :options="convrrtLandingSites"
                        @input="loadConvrrtLandingPagesDropdown"
                    >
                        <template #help>
                            <span v-if="submitted && !isValidLandingSite" data-qa="convrrt-landing-site-required">
                                {{ $t('funnel.edit.goal.convrrt.page.builder.site.requiredErr') }}
                            </span>
                        </template>
                    </DsMultiselect>
                    <DsOutlineButton
                        data-qa="edit-convrrt-landing-site"
                        class="edit-convrrt-landing-site"
                        as="a"
                        :disabled="!isValidLandingSite"
                        @click="editConvrrtLandingSite"
                    >
                        <span>
                            {{ $t('funnel.edit.goal.convrrt.page.builder.edit') }}
                        </span>
                    </DsOutlineButton>
                </section>
                <DsMultiselect
                    v-model="selectedConvrrtLandingPages"
                    data-qa="convrrt-landing-page"
                    class="select-convrrt-landing-page"
                    multiple
                    required
                    bind-value-only
                    :force-invalid="submitted && !isValidLandingPage"
                    :readonly="!isValidLandingSite"
                    :placeholder="$t('funnel.edit.goal.convrrt.page.builder.pages.placeholder')"
                    :loading="loadingConvrrtLandingSites || loadingConvrrtLandingPages"
                    :options="convrrtLandingPages"
                    @input="landingPagesValueInput"
                >
                    <template #help>
                        <span v-if="submitted && !isValidLandingPage" data-qa="convrrt-landing-page-required">
                            {{ $t('funnel.edit.goal.convrrt.page.builder.pages.requiredErr') }}
                        </span>
                    </template>
                </DsMultiselect>

                <section v-if="isConvrrtProAddOn" class="convrrt-page-builder-links">
                    <a
                        data-qa="create-convrrt-landing-page-link"
                        :title="$t('funnel.edit.goal.convrrt.page.builder.create')"
                        class="create-convrrt-landing-page-link"
                        @click="openConvrrtLandingSite"
                    >
                        {{ $t('funnel.edit.goal.convrrt.page.builder.create') }}
                        <DsIcon name="external-link" />
                    </a>
                    <a
                        data-qa="manage-convrrt-landing-page-link"
                        :title="$t('funnel.edit.goal.convrrt.page.builder.manage')"
                        class="manage-convrrt-landing-page-link"
                        @click="openConvrrtLandingSite"
                    >
                        {{ $t('funnel.edit.goal.convrrt.page.builder.manage') }}
                        <DsIcon name="external-link" />
                    </a>
                </section>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';
import {
    getConvrrtLandingSites,
    getConvrrtLandingPages,
    getConvrrtCookieConfig,
    getLeadBucketIdsByPageIds,
    deleteLeadBucketIdsByIds,
    saveNewLeadbucketFunnelMapping,
    getConvrrtUrl,
} from './convrrt.api';

export default {
    components: {
        GoalModal,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    convrrtLandingPageId: '',
                    leadBucketIds: [],
                    leadBucketFunnelMapId: [],
                },
                id: {
                    type: Number,
                },
            }),
        },
        funnelId: {
            type: Number,
            required: true,
        },
        convrrtTokenExists: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['close-modal', 'update-cell-value', 'set-convrrt-token-exists'],

    data() {
        return {
            convrrtLandingSites: [],
            convrrtLandingPages: [],
            existingLandingPages: [],
            loadingConvrrtLandingSites: false,
            loadingConvrrtLandingPages: false,
            selectedConvrrtLandingSite: '',
            selectedConvrrtLandingPages: [],
            selectedConvrrtLandingPagesCopy: [], // formValuesCopy: [],
            selectedConvrrtLandingPagesToDelete: [], // formValuesToDelete: [],
            newSelectedConvrrtLandingPages: [], // newFormValues: [],
            errorMessage: '',
            submitted: false,
        };
    },

    computed: {
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },

        isConvrrtProAddOn() {
            return this.appInfo?.convrrtProAddOn;
        },

        isValidLandingSite() {
            return !!this.selectedConvrrtLandingSite;
        },

        isValidLandingPage() {
            return !!this.selectedConvrrtLandingPages && this.selectedConvrrtLandingPages.length !== 0;
        },

        isReady() {
            return this.isValidLandingSite && this.isValidLandingPage;
        },
    },

    async mounted() {
        this.selectedConvrrtLandingSite = this.cell?.value?.convrrtLandingPageId;
        this.selectedConvrrtLandingPages = this.cell?.value?.leadBucketIds;
        this.selectedConvrrtLandingPagesCopy = this.selectedConvrrtLandingPages ? [...this.selectedConvrrtLandingPages] : [];
        await this.loadConvrrtLandingSitesDropdown();
        window.addEventListener('convrrtFocus', this.handleFocus);
    },

    unmounted() {
        window.removeEventListener('convrrtFocus', this.handleFocus);
    },

    methods: {
        async handleFocus() {
            await this.loadConvrrtLandingSitesDropdown();
        },

        closeModal() {
            this.$emit('close-modal');
        },

        async updateConfig() {
            this.submitted = true;

            if (this.isReady) {
                const cell = { ...this.cell };
                let leadBucketToRemove = [];
                let leadBucketFunnelMapIdsByPageId = [];
                let leadBucketIdsToRemove = [];
                let selectedConvrrtLandingPagesToAdd = [];
                const isEditing = cell.value.convrrtLandingPageId !== '';

                if (isEditing) {
                    leadBucketToRemove = this.getLeadBucketToRemove();
                    selectedConvrrtLandingPagesToAdd = this.getFormsToAdd();

                    if (leadBucketToRemove.length > 0) {
                        leadBucketFunnelMapIdsByPageId = await getLeadBucketIdsByPageIds(leadBucketToRemove);

                        if (leadBucketFunnelMapIdsByPageId.length > 0) {
                            // Retrieves the leadBucketIds for this specific goal that need to be removed
                            leadBucketIdsToRemove = this.getLeadBucketFunnelMapIdsToRemove(leadBucketFunnelMapIdsByPageId, cell.value);

                            await deleteLeadBucketIdsByIds(leadBucketIdsToRemove);
                        }
                    }
                }

                cell.value.leadBucketIds = this.selectedConvrrtLandingPages;

                const leadBucketFunnelMapIds = [];

                // The following validation removes from the cellValue.leadBucketFunnelMapId the values that
                // are no longer used
                /* eslint-disable  no-continue */
                if (leadBucketIdsToRemove.length > 0 && cell.value.leadBucketFunnelMapId) {
                    cell.value.leadBucketFunnelMapId.forEach((item) => {
                        if (!leadBucketIdsToRemove.includes(Number(item))) {
                            leadBucketFunnelMapIds.push(item);
                        }
                    });

                    cell.value.leadBucketFunnelMapId = leadBucketFunnelMapIds;
                }

                // This validation means that when the Landing Page Dropdown was changed
                // the leadBucketFunnelMapId should be empty in order to set the new values.
                if (cell.value.convrrtLandingPageId !== this.selectedConvrrtLandingSite) {
                    cell.value.leadBucketFunnelMapId = [];
                } else {
                    const isOldConfiguration = !Array.isArray(cell.value.leadBucketFunnelMapId);

                    if (isOldConfiguration) {
                        const tempBucketId = cell.value.leadBucketFunnelMapId;

                        cell.value.leadBucketFunnelMapId = [];
                        cell.value.leadBucketFunnelMapId.push(tempBucketId);
                    }
                }

                cell.value.convrrtLandingPageId = this.selectedConvrrtLandingSite;

                // If the goal is being edited then we just want to add the leadBucketConfig for the new added forms
                // otherwise add the leadBucketConfig for all the selected forms
                if (isEditing) {
                    selectedConvrrtLandingPagesToAdd.forEach((item) => {
                        this.saveLeadBucketConfig(item, cell.value);
                    });
                } else {
                    this.selectedConvrrtLandingPages.forEach((item) => {
                        this.saveLeadBucketConfig(item, cell.value);
                    });
                }
                const eventData = {
                    cell,
                };

                this.$emit('update-cell-value', eventData);
            }
        },

        async saveLeadBucketConfig(leadBucketId, cellValue) {
            const dataConfig = {
                funnelId: this.funnelId,
                leadBucketId,
            };

            try {
                const response = await saveNewLeadbucketFunnelMapping(dataConfig);
                let leadBucketFunnelMapCopy = [];

                if (cellValue.leadBucketFunnelMapId && cellValue.leadBucketFunnelMapId instanceof String && cellValue.leadBucketFunnelMapId.length > 0) {
                    leadBucketFunnelMapCopy.push(cellValue.leadBucketFunnelMapId);
                } else if (cellValue.leadBucketFunnelMapId && cellValue.leadBucketFunnelMapId.length > 0) {
                    leadBucketFunnelMapCopy = cellValue.leadBucketFunnelMapId;
                }

                leadBucketFunnelMapCopy.push(response.data);
                cellValue.leadBucketFunnelMapId = leadBucketFunnelMapCopy;
            } catch (error) {
                // show toast with error
            }
        },

        getLeadBucketToRemove() {
            const bucketsToDelete = [];

            this.selectedConvrrtLandingPagesCopy.forEach((item) => {
                if (!this.newSelectedConvrrtLandingPages.includes(item)) {
                    bucketsToDelete.push(item);
                }
            });

            return bucketsToDelete;
        },

        getLeadBucketFunnelMapIdsToRemove(leadBucketFunnelMapIdsByPageId, cellValue) {
            const idsToRemove = [];

            leadBucketFunnelMapIdsByPageId.forEach((item) => {
                if (!this.selectedConvrrtLandingPagesCopy.includes(item)) {
                    const stringValue = item.toString();

                    if (Array.isArray(cellValue.leadBucketFunnelMapId)) {
                        if (cellValue.leadBucketFunnelMapId.includes(stringValue) || cellValue.leadBucketFunnelMapId.includes(item)) {
                            idsToRemove.push(item);
                        }
                    } else {
                        // eslint-disable-next-line no-lonely-if
                        if (cellValue.leadBucketFunnelMapId === stringValue || cellValue.leadBucketFunnelMapId === item) {
                            idsToRemove.push(item);
                        }
                        // eslint-enable-next-line no-lonely-if
                    }
                }
            });

            return idsToRemove;
        },

        getFormsToAdd() {
            const getFormsToAdd = [];

            this.newSelectedConvrrtLandingPages.forEach((item) => {
                if (!this.selectedConvrrtLandingPagesCopy.includes(item)) {
                    getFormsToAdd.push(item);
                }
            });

            return getFormsToAdd;
        },

        async loadConvrrtLandingSitesDropdown() {
            try {
                this.loadingConvrrtLandingSites = true;

                this.convrrtLandingSites = await getConvrrtLandingSites();

                if (this.selectedConvrrtLandingSite) {
                    this.loadingConvrrtLandingPages = true;
                    this.convrrtLandingPages = await getConvrrtLandingPages(this.selectedConvrrtLandingSite);

                    this.selectedConvrrtLandingPages = this.cell?.value?.leadBucketIds || [];
                    this.selectedConvrrtLandingPagesCopy = [...this.selectedConvrrtLandingPages];
                }
            } catch (e) {
                // do nothing
            } finally {
                this.loadingConvrrtLandingSites = false;

                if (this.selectedConvrrtLandingSite) {
                    this.loadingConvrrtLandingPages = false;
                }
            }
        },

        async loadConvrrtLandingPagesDropdown() {
            this.submitted = false;

            try {
                this.loadingConvrrtLandingPages = true;

                if (this.selectedConvrrtLandingSite) {
                    this.convrrtLandingPages = await getConvrrtLandingPages(this.selectedConvrrtLandingSite);
                }
                this.selectedConvrrtLandingPages = [];
            } finally {
                this.loadingConvrrtLandingPages = false;
            }
        },

        async editConvrrtLandingSite() {
            await this.createConvrrtCookie();
            const url = getConvrrtUrl(this.selectedConvrrtLandingSite, this.cell.id, this.funnelId);

            window.open(url, '_target');
        },

        async openConvrrtLandingSite() {
            await this.createConvrrtCookie();
            const url = getConvrrtUrl('', this.cell.id, this.funnelId);

            window.open(url, '_target');
        },

        async createConvrrtCookie() {
            if (!this.convrrtTokenExists) {
                try {
                    const data = await getConvrrtCookieConfig();

                    window.document.cookie = data;

                    this.$emit('set-convrrt-token-exists');
                } catch (error) {
                    document.dispatchEvent(new CustomEvent('user-notification', {
                        message: 'Error occurred authenticating user.',
                        type: 'error',
                    }));
                }
            }
        },

        landingPagesValueInput(value) {
            this.newSelectedConvrrtLandingPages = [];
            this.newSelectedConvrrtLandingPages = value;
        },
    },
};
</script>

<style lang="scss" scoped>
.select-convrrt-landing-page::v-deep .selected-label{
    opacity: 0;
}

.edit-convrrt-landing-site {
    width: px-to-rem(195);
    height: px-to-rem(40);
    margin-left: $spacing-025;
}
.convrrt-landing-site-select, .convrrt-page-builder-links {
    display: flex;
}
.create-convrrt-landing-page-link, .manage-convrrt-landing-page-link {
    --icon-size: #{px-to-rem(13.33px)};
}
a.button.edit-convrrt-landing-site.outline {
    margin-left: $spacing-125;
}

a.create-convrrt-landing-page-link {
    margin-left: $spacing-050;
    margin-right: $spacing-300;
}
</style>
